import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Observable} from 'rxjs';
import {Order} from '../_models/order';
import {LineItem} from '../_models/lineItem';
import {Modification} from '../_models/modification';
import {PrinterPayload} from '../_models/PrinterPayload';
import {AESEncryptDecryptService} from './aesencrypt-decrypt.service';
import {Discount} from '../_models/discount';
import {UtilsService} from './utils.service';

@Injectable({providedIn: 'root'})
export class CloverOrderService {
    constructor(private http: HttpClient, private _AESEncryptDecryptService: AESEncryptDecryptService, private utilsService: UtilsService) {
    }

    postNewOrder(order: Order): Observable<Order> {
        console.log('in CloverOrderService post New Order'); // *MES*
        const body = {
            'order': order
        };
        return this.http.post<Order>(`${environment.apiUrl}/cloverOrders/createOrder`, body);
    }

    jukeboxOrder(order: Order, playlist: any[]): Observable<any> {
        console.log('in CloverOrderService post New jukebox Order'); // *MES*
        const body = {
            'order': order,
            'playlist': playlist
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/jukeboxOrder`, body);
    }


    getOrder(id: string) {

        const body = {
            'orderId': id
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/getOrder`, body);
    }

    /**
     * Same as getOrder but also expands the payments collection so callers can
     * verify a Clover payment webhook belongs to this order before printing.
     * Used by the cashier-register Clover payment listener (fix/clover-payment-receipt-correlation).
     */
    getOrderWithPayments(id: string) {
        const body = {
            'orderId': id
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/getOrderWithPayments`, body);
    }

    getOrderHistoryById(id: string) {

        const body = {
            'orderId': id
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/getOrderHistoryById`, body);
    }

    getOrderByDate(maxDate: Date, minDate: Date) {

        const date = new Date();
        const body = {
            'minDate': Number(minDate.getTime().toFixed(0)),
            'maxDate': Number(maxDate.getTime().toFixed(0)),
            'timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/getOrderBetweenDatesWithLineItems`, body);
    }

    closeTableOrder(id: string, table: string) {
        const order = new Order();
        order.id = id;
        // order.title = table + ' Paid';
        order.title = table;
        order.paymentState = 'PAID';
        order.state = 'locked';

        const body = {
            'orderId': id,
            'order': order
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/closeTableOrder`, body);
    }

    updateOrder(order: Order) {

        const body = {
            'order': order
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/closeTableOrder`, body);
    }

    updateOrderNote(orderId: string, note: string) {
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/updateOrderNote`, {orderId, note});
    }

    getUnpaidOrder(tableName: string) {
        const body = {
            'tableName': tableName //+ '&filter=clientCreatedTime>=' + Math.floor(unixTime),
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/getUnpaidOrder`, body);
    }

    getServerTableOrders(orderIds: string[], tableName?: string) {
        const body = {
            'orderIds': orderIds,
            // 'tableName': tableName,
            // 'useTableName': false
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/getServerTableOrders`, body);
    }

    moveTable(tableName: string, updatedTableName: string) {
        const body = {
            'tableName': tableName,
            'updatedTableName': updatedTableName
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/moveTable`, body);
    }

    applyServiceChargeToOrder(orderId, serviceCharge) {
        const body = {
            'orderId': orderId,
            'serviceCharge': serviceCharge
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/applyServiceChargeToOrder`, body);
    }

    applyServiceChargeToAllOrders(orders, serviceCharge) {
        const body = {
            'orders': orders,
            'serviceCharge': serviceCharge
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/applyServiceChargeToMultipleOrders`, body);
    }

    getOrderLineItems(id: string) {

        const body = {
            'orderId': id
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/getOrderLineItems`, body);
    }

    // not used as it gets all the orders and line items for a table.
    getOrdersAndLineItems(tableName: string, merchantId: string) {
        const cloverToken = this.utilsService.getCurrentUser()?.cloverToken;
        const body = {
            // 'merchant_id': localStorage.getItem('merchant_id'),
            'merchantId': merchantId,
            'token': cloverToken,
            'note': tableName
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/getOrdersAndLineItems`, body);
    }

    postNewLineItem(id: string, lineItem: LineItem) {

        const body = {
            'orderId': id,
            'lineItem': lineItem
        };

        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/createLineItem`, body);
    }

    fireOrder(id: string, v1?, totals?: { subtotal: number; tax: number; tip: number; total: number }, businessName?: string, trigger?: 'on_save' | 'on_pay' | 'after_payment', manual?: boolean) {
        const body: any = {
            'printItem': new PrinterPayload('PrintOrder', id),
            'orderId': id,
            'v1Print': v1 ? v1 : false
        };
        // Authoritative totals from the cashier UI. Forwarded to the
        // backend so when the DineNGo-receipt-printer gate fires it can
        // hand them straight to the receipt builder — bypassing the
        // corrupted `cloverOrder.taxAmount` we've seen on some atomic
        // orders. Bug A: pre-fix this missing channel meant tax printed
        // as $0 even though the cashier saw the right tax on screen.
        if (totals) body.totals = totals;
        if (businessName) body.businessName = businessName;
        // trigger routes to order printers whose fire_trigger matches.
        // Omitting trigger defaults the backend to 'on_pay' (backward compat).
        if (trigger) body.trigger = trigger;
        // manual=true bypasses auto_print_disabled suppression on the backend
        // so the cashier's explicit "Print Receipt" tap always reaches V1/V2.
        if (manual) body.manual = true;
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/firePrint`, body);
    }

    /**
     * Reprint a paid order's receipt on the merchant's connected Clover device.
     * Backend route: POST /cloverOrders/reprintReceipt — body { orderId, deviceId? }
     *
     *  - Without deviceId, Clover routes to the merchant's primary printer
     *    (same path used by the auto-print on PAID).
     *  - With deviceId, the print job is delivered to that specific Mini/Flex
     *    via Clover's device notifications API — used by the picker fallback.
     */
    reprintReceipt(orderId: string, deviceId?: string, totals?: { subtotal: number; tax: number; tip: number; total: number }, businessName?: string) {
        const body: any = {orderId};
        if (deviceId) body.deviceId = deviceId;
        if (totals) body.totals = totals;
        if (businessName) body.businessName = businessName;
        return this.http.post<{success: boolean; deliveredVia?: string; reason?: string; deviceId?: string; error?: string}>(
            `${environment.apiUrl}/cloverOrders/reprintReceipt`, body,
        );
    }

    /**
     * Print (or reprint) the day's closeout summary receipt on the merchant's
     * configured receipt printer. Backend route:
     *   POST /cloverOrders/printCloseoutReceipt — body { date, summary?, trigger? }
     *
     * `trigger`:
     *   - 'auto'    : fired automatically by cashier-register's triggerCloseout.
     *   - 'reprint' : fired manually from the Closeouts tab "Reprint" button.
     * `summary` is optional — when omitted the backend rebuilds it from
     * cloverOrderService.getOrderByDate(date) so the UI never has to recompute.
     */
    printCloseoutReceipt(date: string, summary?: any, trigger: 'auto' | 'reprint' = 'reprint') {
        const body: any = {date, trigger};
        if (summary) body.summary = summary;
        return this.http.post<{success: boolean; reason?: string; error?: string; deliveredVia?: string}>(
            `${environment.apiUrl}/cloverOrders/printCloseoutReceipt`, body,
        );
    }

    /**
     * Read-only reconciliation between Clover's per-day payment record and
     * the DineNGo Closeouts-tab summary. Backend route:
     *   POST /cloverOrders/getCloseoutReconciliation — body { date, summary? }
     *
     * Returns side-by-side totals + per-tender deltas + a status flag
     * ('match' | 'mismatch' | 'no_clover_data' | 'no_dinengo_data').
     * Triggers no settlement — purely informational.
     */
    getCloseoutReconciliation(date: string, summary?: any) {
        const body: any = {date};
        if (summary) body.summary = summary;
        return this.http.post<{
            success: boolean;
            reason?: string;
            error?: string;
            report?: {
                dateStr: string;
                clover:  {paymentCount: number; grossCents: number; tipCents: number;
                          byTender: Array<{label: string; count: number; totalCents: number}>};
                dinengo: {orderCount: number; grossCents: number; tipCents: number;
                          byMethod: Array<{method: string; count: number; totalCents: number}>} | null;
                deltas:  {grossCents: number; tipCents: number;
                          byTender: Array<{tender: string; cloverCents: number;
                                           dinengoCents: number; deltaCents: number}>} | null;
                status:  'match' | 'mismatch' | 'no_clover_data' | 'no_dinengo_data';
            };
        }>(`${environment.apiUrl}/cloverOrders/getCloseoutReconciliation`, body);
    }

    /** Returns the merchant's Clover devices for the reprint device-picker fallback. */
    listMerchantDevices() {
        return this.http.get<{devices: Array<{id: string; name: string | null; model: string | null; serial: string | null}>}>(
            `${environment.apiUrl}/cloverOrders/listDevices`,
        );
    }

    payOrderOnDevice(orderId: string, total: number, deviceId: string, deviceSerial: string, firePrint: boolean, receiptEnabled: boolean = true) {
        const body = {
            'firePrint': firePrint,
            'total': total,
            'deviceSerial': deviceSerial,
            'deviceId': deviceId,
            'orderId': orderId,
            'receiptEnabled': receiptEnabled
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/payOrderOnDevice`, body);
    }


    openOrderPaymentByMerchantId(id: string) {
        const body = {
            'orderId': id
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/openOrderOnStation`, body);
    }

    openOrderPaymentByDeviceId(orderId: string, deviceId: string, deviceSerial: string, total?: number) {
        const body: any = {
            'deviceId': deviceId,
            'deviceSerial': deviceSerial,
            'orderId': orderId
        };
        if (total != null) {
            body.total = total;
        }
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/openOrderPaymentOnDevice`, body);
    }

    deleteLineItem(id: string, lineItemId: string) {
        const body = {
            'orderId': id,
            'lineItemId': lineItemId
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/deleteLineItem`, body);
    }

    deleteOrder(orderId: string) {
        return this.http.delete<any>(`${environment.apiUrl}/cloverOrders/deleteOrder/${orderId}`);
    }

    /**
     * Add Customer (Clover-style) — resolves a single phone OR email
     * string to a Clover customer (find or create) and attaches them
     * to the open order so the customer appears on the receipt + Clover
     * dashboard. Server returns { customer: { id, name, primaryPhone,
     * primaryEmail } } for the cashier UI to render + drive rewards.
     */
    attachCustomer(orderId: string, input: string) {
        return this.http.post<{ customer: { id: string; name: string | null; primaryPhone: string | null; primaryEmail: string | null } }>(
            `${environment.apiUrl}/cloverOrders/attachCustomer`,
            { orderId, input },
        );
    }

    /**
     * Bug 3: Tell the connected Clover device to abort an in-progress
     * payment so it doesn't stay stuck on the payment screen after the
     * cashier closes the "waiting for device" dialog.
     */
    cancelPaymentOnDevice(orderId: string, deviceId: string, deviceSerial: string) {
        const body = {
            orderId,
            deviceId,
            deviceSerial
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/cancelPaymentOnDevice`, body);
    }

    /**
     * Closeout (Batch) — admin/manager-only. Asks the connected Clover
     * device to launch its native closeout screen, which both settles
     * the open batch and prints the closeout/settlement receipt.
     * Backend route: POST /cloverOrders/batchCloseout — body { deviceId, deviceSerial }
     */
    batchCloseout(deviceId: string, deviceSerial: string) {
        const body = {
            deviceId,
            deviceSerial
        };
        return this.http.post<{success: boolean; usedNotification?: boolean; error?: string}>(
            `${environment.apiUrl}/cloverOrders/batchCloseout`, body,
        );
    }

    postNewModification(id: string, lineItemID: string, modification: Modification) {
        const body = {
            'orderId': id,
            'lineitem_id': lineItemID,
            'modification': modification
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/createItemModification`, body);
    }

    getAiInsights(payload: {
        totalSales: number;
        topItems: { name: string; count: number }[];
        topPairs: { itemA: string; itemB: string; count: number }[];
        hourlyPatterns: Record<string, number>;
        weekdayPatterns: Record<string, number>;
    }): Observable<{ title: string; insight: string; action: string; icon: string }[]> {
        return this.http.post<{ title: string; insight: string; action: string; icon: string }[]>(
            `${environment.apiUrl}/cloverOrders/getAiInsights`, payload
        );
    }

    postNewDiscount(order_Id, discount: Discount) {
        const body = {
            'orderId': order_Id,
            'discount': discount
        };
        return this.http.post<any>(`${environment.apiUrl}/cloverOrders/postNewDiscount`, body);
    }
}
